import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";
import { Currency } from "commonComponents/financial/Currency/Currency";
import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";

import {
    type CurrencyCustomFieldDefinition,
    type CurrencyCustomFieldValue,
    type CustomFieldAssociatedType,
} from "handlers";

export const CurrencyCustomFieldHandler: ICustomFieldTypeHandler<
    number,
    CurrencyCustomFieldValue,
    CurrencyCustomFieldDefinition
> = {
    getValue(field): number | null {
        return field.value ?? null;
    },

    setValue(field, value): CurrencyCustomFieldValue {
        return { ...field, value: value ?? 0 };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: number | null,
    ): CurrencyCustomFieldValue {
        return {
            dataType: "Currency",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? 0,
        };
    },

    compare(valueA: number | null, valueB: number | null): number {
        const a = valueA ?? 0;
        const b = valueB ?? 0;
        return a - b;
    },

    renderDisplay(value: number | null): ReactNode {
        if (value === null || value === undefined) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }
        return <CurrencyDisplay value={value} data-testid="currencyCustomFieldDisplay" />;
    },

    renderEditor(
        value: number | null,
        customFieldId: number,
        onChange: (newValue: number | null) => void,
        onBlur: () => void,
        autoFocus?: boolean,
    ): ReactNode {
        return (
            <div className="custom-field-currency-editor">
                <Currency
                    id={`currencyCustomFieldEditor-${customFieldId}`}
                    data-testid="currencyCustomFieldEditor"
                    value={value}
                    onChange={(_field, val) => onChange(val ?? null)}
                    onBlur={() => onBlur()}
                    formMode={autoFocus ? "always-editing" : "click-to-edit"}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={autoFocus}
                />
            </div>
        );
    },

    getAgGridCellEditor() {
        return undefined;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
